import { useState, useEffect, useRef } from 'react';
import { API } from '@/App';
import { experienceJSON } from '../../utils/experienceRequest';

export const useDiscoverySearch = (query, category = '', options = {}) => {
  const {scopeKey = '', limit = 30, connected = false} = options;
  const token = localStorage.getItem('session_token') || '';
  const term = query.trim();
  const owner = `${scopeKey}:${token}`;
  const signature = `${owner}:${category}:${term}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingClinical, setCheckingClinical] = useState(false);
  const [checkingCompanies, setCheckingCompanies] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [connection, setConnection] = useState(null);
  const sequence = useRef(0);
  const extensionSequence = useRef(0);
  const connectionId = useRef(0);
  const consumed = useRef(new Set());
  useEffect(() => {
    const id = ++sequence.current;
    const controller = new AbortController();
    setData(null); setError(null);
    if (term.length < 2) {setLoading(false); return () => controller.abort();}
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const body = await experienceJSON(`${API}/search/unified-autocomplete?q=${encodeURIComponent(term)}&limit=${limit}&clinical=false${category ? `&category=${encodeURIComponent(category)}` : ''}`, {
          signal: controller.signal, deadlineMs: 6000, headers: {Authorization: `Bearer ${token}`},
        });
        if (id === sequence.current && !controller.signal.aborted) setData({...body, query:term, _scope:owner, _signature:signature, _category:category});
      } catch (e) {
        if (id === sequence.current && !controller.signal.aborted && e.name !== 'AbortError') setError({signature, message:e.code === 'TIMEOUT' ? 'Catalog lookup took too long. Your query is preserved; retry.' : e.message});
      } finally {if (id === sequence.current && !controller.signal.aborted) setLoading(false);}
    }, 220);
    return () => {clearTimeout(timer); controller.abort();};
  }, [term, attempt, category, token, scopeKey, limit, owner, signature]);

  // Connected discovery is an explicit full-search action. Ordinary typing only
  // reads saved identities. Slow providers never hold catalog matches hostage.
  useEffect(() => {
    const id = ++extensionSequence.current;
    const controller = new AbortController();
    setCheckingClinical(false); setCheckingCompanies(false);
    const requested = connected || (connection?.term === term && connection?.category === category && connection?.owner === owner);
    if (!requested || data?._signature !== signature || term.length < 3 || term.length > 120) return () => controller.abort();
    const requestKey = connection?.term === term && connection?.category === category && connection?.owner === owner ? `click:${connection.id}` : `explicit:${signature}`;
    if (consumed.current.has(requestKey)) return () => controller.abort();
    consumed.current.add(requestKey);
    if (consumed.current.size > 100) consumed.current.delete(consumed.current.values().next().value);
    const publish = patch => {
      if (id === extensionSequence.current && !controller.signal.aborted) setData(previous => previous?._signature === signature ? {...previous, ...patch} : previous);
    };
    const headers = {Authorization: `Bearer ${token}`};
    const jobs = [];
    if (!category || category === 'companies') {
      setCheckingCompanies(true);
      jobs.push((async () => {
        try {
          const result = await experienceJSON(`${API}/search/company-candidates?q=${encodeURIComponent(term)}${connection?.attempt > 1 ? '&refresh=true' : ''}`, {signal:controller.signal, deadlineMs:10000, headers});
          publish({external_companies:result});
        } catch (e) {
          if (!controller.signal.aborted) publish({external_companies:{status:e.code === 'AUTH_REQUIRED' ? 'auth_required' : 'unavailable', candidates:[], limitation:e.code === 'AUTH_REQUIRED' ? 'Sign in to check connected company sources.' : 'Connected company lookup is unavailable. Saved matches remain available; retry.'}});
        } finally {if (id === extensionSequence.current) setCheckingCompanies(false);}
      })());
    }
    if (!category || category === 'assets') {
      setCheckingClinical(true);
      jobs.push((async () => {
        try {
          const result = await experienceJSON(`${API}/search/clinical-candidates?q=${encodeURIComponent(term)}`, {signal:controller.signal, deadlineMs:10000, headers});
          if (id === extensionSequence.current && !controller.signal.aborted) setData(previous => {
            if (previous?._signature !== signature) return previous;
            const seen = new Set((previous.assets || []).map(row => (row.name || row.label || '').toLowerCase()));
            const additions = (result.assets || []).filter(row => !seen.has((row.name || row.label || '').toLowerCase()));
            return {...previous, assets:[...(previous.assets || []), ...additions], clinical_lookup:result.clinical_lookup || result, auto_open:null};
          });
        } catch (e) {if (!controller.signal.aborted) publish({clinical_lookup:{status:'unavailable', limitation:e.code === 'AUTH_REQUIRED' ? 'Sign in to check clinical sources.' : 'Clinical lookup is unavailable. Saved matches remain available; retry.'}});}
        finally {if (id === extensionSequence.current) setCheckingClinical(false);}
      })());
    }
    Promise.allSettled(jobs);
    return () => controller.abort();
  }, [term, category, token, scopeKey, data?.query, data?._scope, connection, connected, owner, signature]);
  const checkConnected = () => setConnection(previous => ({term, category, owner, id:++connectionId.current, attempt:(previous?.term === term ? previous.attempt : 0) + 1}));
  const visible = data?._signature === signature ? data : null;
  const visibleError = error?.signature === signature ? error.message : '';
  return {data:visible, loading:term.length >= 2 && (loading || (!visible && !visibleError)), checkingClinical, checkingCompanies, error:visibleError, checkConnected, retry:() => setAttempt(n => n+1)};
};

// Optional telemetry must never prevent navigation, even when browser storage
// or the UUID API is unavailable. Never include the research question here.
export const journey = (event, fields = {}) => {
  try {
    const journey_id = sessionStorage.getItem('bds_discovery_journey') || globalThis.crypto?.randomUUID?.();
    if (!journey_id) return;
    sessionStorage.setItem('bds_discovery_journey', journey_id);
    if (event === 'report_opened') sessionStorage.setItem('bds_last_report_open', String(Date.now()));
    const {query, question, ...metadata} = fields;
    fetch(`${API}/discovery/events`, {method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('session_token')}`},
      body:JSON.stringify({event, journey_id, ...metadata})}).catch(() => {});
  } catch { /* Browsing works when optional telemetry is blocked. */ }
};
